import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Form, Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import Select, { type SingleValue } from 'react-select';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import OptionLayoutIcon from '@/app/common/components/OptionsLayout/OptionLayoutIcon';
import SingleValueIcon from '@/app/common/components/OptionsLayout/SingleValueIcon';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { CHANNEL_BOTS_ACTIVATE_URL } from '@/app/Indexer/components/ScheduleMessengerSelect';
import {
  defaultDeviceOption,
  defaultInternetOption,
  defaultPlaceOption,
  deviceOptions,
  internetSelectOptions,
  type DeviceOption,
  type InternetOption,
  type PlaceOption
} from '@/app/PageSpeed/config/analysisFormOptions';
import useAnalysisPlaceOptions from '@/app/PageSpeed/hooks/useAnalysisPlaceOptions';

export type AnalysisScheduleEntry = {
  url: string;
  location_code: string;
  device_type: string;
  internet_type: string;
};

export type AnalysisScheduleConfig = {
  entries: AnalysisScheduleEntry[];
  notification_method: string[];
  day: string;
  enabled: boolean;
};

type ScheduleEntryRow = AnalysisScheduleEntry & { id: string };

type InitialSettings = {
  location_code?: string;
  device_type?: string;
  internet_type?: string;
};

type InitialSchedule = {
  urls: AnalysisScheduleEntry[];
  notification_method?: string | string[];
  scheduled_day?: string;
  enabled?: boolean;
};

type Props = {
  show: boolean;
  initialUrls?: string[];
  initialSettings?: InitialSettings;
  initialSchedule?: InitialSchedule;
  onHide: () => void;
  onSave?: (config: AnalysisScheduleConfig) => void;
};

const MAX_SCHEDULE_URLS = 50;

const weekDays = [
  ['saturday', 'شنبه'],
  ['sunday', 'یکشنبه'],
  ['monday', 'دوشنبه'],
  ['tuesday', 'سه‌شنبه'],
  ['wednesday', 'چهارشنبه'],
  ['thursday', 'پنجشنبه'],
  ['friday', 'جمعه']
] as const;

const toPersianDigits = (value: string | number) =>
  String(value).toPersianDigit?.() ?? String(value);

const isValidHttpUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

let entrySeq = 0;
const nextEntryId = () => `schedule-url-${++entrySeq}`;

const createEntry = (
  defaults: InitialSettings,
  url = ''
): ScheduleEntryRow => ({
  id: nextEntryId(),
  url,
  location_code: defaults.location_code || defaultPlaceOption.value,
  device_type: defaults.device_type || defaultDeviceOption.value,
  internet_type: defaults.internet_type || defaultInternetOption.value
});

const compactSelectStyles = {
  control: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    minHeight: '32px',
    height: '32px',
    paddingLeft: 0,
    paddingRight: 4,
    boxShadow: 'none !important',
    cursor: 'pointer',
    borderRadius: 6
  }),
  valueContainer: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    height: '32px',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 8
  }),
  indicatorsContainer: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    height: '32px'
  }),
  dropdownIndicator: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    padding: 4
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    margin: 0,
    padding: 0
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 2000
  })
};

type SectionHeadingProps = {
  icon: IconProp;
  title: string;
  description: string;
  aside?: ReactNode;
};

const SectionHeading = ({
  icon,
  title,
  description,
  aside
}: SectionHeadingProps) => (
  <div className="analysis-schedule-section-heading">
    <span className="analysis-schedule-section-icon" aria-hidden="true">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="flex-grow-1">
      <div className="fw-semibold">{title}</div>
      <div className="text-600 fs--2 mt-1">{description}</div>
    </div>
    {aside}
  </div>
);

const AnalysisScheduleDrawer = ({
  show,
  initialUrls = [],
  initialSettings = {},
  initialSchedule,
  onHide,
  onSave
}: Props) => {
  const placeSelectOptions = useAnalysisPlaceOptions();
  const { data: userInfo, isSuccess, isLoading } = useGetUserInfoQuery();
  const [entries, setEntries] = useState<ScheduleEntryRow[]>(() => [
    createEntry(initialSettings)
  ]);
  const [telegram, setTelegram] = useState(true);
  const [bale, setBale] = useState(true);
  const [day, setDay] = useState('saturday');
  const [enabled, setEnabled] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const joinTelegram = Boolean(userInfo?.data?.join_telegram);
  const joinBale = Boolean(userInfo?.data?.join_bale);

  const defaults = useMemo(
    () => ({
      location_code: initialSettings.location_code || defaultPlaceOption.value,
      device_type: initialSettings.device_type || defaultDeviceOption.value,
      internet_type: initialSettings.internet_type || defaultInternetOption.value
    }),
    [
      initialSettings.location_code,
      initialSettings.device_type,
      initialSettings.internet_type
    ]
  );

  const initialUrlKey = initialUrls
    .map(url => url.trim())
    .filter(Boolean)
    .join('\n');

  useEffect(() => {
    if (!show) return;
    const scheduleEntries = initialSchedule?.urls;
    const urls = initialUrlKey ? initialUrlKey.split('\n') : [''];
    setEntries(
      scheduleEntries?.length
        ? scheduleEntries.map(entry => ({
            ...createEntry(entry, entry.url),
            location_code: entry.location_code,
            device_type: entry.device_type,
            internet_type: entry.internet_type
          }))
        : urls.map(url => createEntry(defaults, url))
    );
    const methods = Array.isArray(initialSchedule?.notification_method)
      ? initialSchedule.notification_method
      : initialSchedule?.notification_method
        ? [initialSchedule.notification_method]
        : ['telegram', 'bale'];
    setTelegram(methods.includes('telegram'));
    setBale(methods.includes('bale'));
    setDay(initialSchedule?.scheduled_day || 'saturday');
    setEnabled(initialSchedule?.enabled ?? true);
    setSubmitted(false);
    setShowValidation(false);
  }, [show, defaults, initialUrlKey, initialSchedule]);

  useEffect(() => {
    if (!isSuccess) return;
    if (!joinTelegram) setTelegram(false);
    if (!joinBale) setBale(false);
  }, [isSuccess, joinTelegram, joinBale]);

  useEffect(() => {
    const firstActive = placeSelectOptions.find(option => !option.isDisabled);
    if (!firstActive) return;
    setEntries(prev =>
      prev.map(entry => {
        const current = placeSelectOptions.find(
          option => option.value === entry.location_code
        );
        if (current && !current.isDisabled) return entry;
        return { ...entry, location_code: firstActive.value };
      })
    );
  }, [placeSelectOptions]);

  const updateEntry = (
    id: string,
    patch: Partial<Omit<ScheduleEntryRow, 'id'>>
  ) => {
    setEntries(prev =>
      prev.map(entry => (entry.id === id ? { ...entry, ...patch } : entry))
    );
    setSubmitted(false);
    setShowValidation(false);
  };

  const addEntry = () => {
    if (entries.length >= MAX_SCHEDULE_URLS) return;
    const last = entries[entries.length - 1];
    setEntries(prev => [
      ...prev,
      createEntry(
        {
          location_code: last?.location_code || defaults.location_code,
          device_type: last?.device_type || defaults.device_type,
          internet_type: last?.internet_type || defaults.internet_type
        },
        ''
      )
    ]);
    setSubmitted(false);
    setShowValidation(false);
  };

  const removeEntry = (id: string) => {
    setEntries(prev =>
      prev.length === 1
        ? [createEntry(defaults)]
        : prev.filter(entry => entry.id !== id)
    );
    setSubmitted(false);
    setShowValidation(false);
  };

  const normalizedEntries = entries
    .map(entry => ({
      ...entry,
      url: entry.url.trim()
    }))
    .filter(entry => entry.url);

  const hasInvalidUrl = normalizedEntries.some(
    entry => !isValidHttpUrl(entry.url)
  );
  const canConfigureNotifications =
    isSuccess && joinTelegram && joinBale && telegram && bale;
  const canSave =
    Boolean(normalizedEntries.length) &&
    !hasInvalidUrl &&
    canConfigureNotifications;
  const missingChannels = [
    !joinTelegram ? 'تلگرام' : '',
    !joinBale ? 'بله' : ''
  ].filter(Boolean);
  const selectedWeekDay =
    weekDays.find(([value]) => value === day)?.[1] || 'شنبه';
  const scheduleSummary = `هر ${selectedWeekDay}`;

  const save = () => {
    setShowValidation(true);
    if (!canSave) return;
    onSave?.({
      entries: normalizedEntries.map(
        ({ url, location_code, device_type, internet_type }) => ({
          url,
          location_code,
          device_type,
          internet_type
        })
      ),
      notification_method: [
        ...(telegram ? ['telegram'] : []),
        ...(bale ? ['bale'] : [])
      ],
      day,
      enabled
    });
    setSubmitted(true);
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="start"
      className="analysis-schedule-drawer"
      onShow={() => {
        document
          .querySelector('.analysis-schedule-drawer')
          ?.removeAttribute('tabindex');
      }}
    >
      <style>
        {`
          .analysis-schedule-drawer {
            width: min(520px, 100%) !important;
            background-color: var(--falcon-body-bg);
          }
          .analysis-schedule-drawer .offcanvas-header {
            padding: 1rem 1.25rem;
          }
          .analysis-schedule-drawer .offcanvas-body {
            padding: 1rem 1.25rem 0.75rem;
          }
          .analysis-schedule-drawer .offcanvas-footer {
            padding: 0.9rem 1.25rem 1.15rem;
            border-top: 1px solid var(--falcon-border-color);
            background: var(--falcon-body-bg);
          }
          .analysis-schedule-section {
            border: 1px solid var(--falcon-border-color);
            border-radius: 12px;
            padding: 0.85rem 1rem;
            background: color-mix(
              in srgb,
              var(--falcon-body-bg, #0b1727) 90%,
              #000 10%
            );
          }
          [data-bs-theme='light'] .analysis-schedule-section {
            background: #fff;
          }
          [data-bs-theme='light'] .analysis-schedule-section-icon {
            color: color-mix(in srgb, var(--falcon-primary) 55%, #fff);
          }
          .analysis-schedule-section-heading {
            display: flex;
            align-items: flex-start;
            gap: 0.65rem;
            margin-bottom: 0.85rem;
          }
          .analysis-schedule-section-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            flex: 0 0 30px;
            border-radius: 8px;
            color: var(--falcon-primary);
            background: var(--falcon-primary-bg-subtle, rgba(0, 172, 169, 0.12));
          }
          .analysis-schedule-count {
            white-space: nowrap;
            border: 1px solid var(--falcon-border-color);
            border-radius: 999px;
            padding: 0.2rem 0.55rem;
            color: var(--falcon-gray-700);
            font-size: 0.7rem;
          }
          .analysis-schedule-entry + .analysis-schedule-entry {
            padding-top: 0.55rem;
            margin-top: 0.55rem;
            border-top: 1px dashed var(--falcon-border-color);
          }
          .analysis-schedule-entry .form-control {
            height: 32px;
            min-height: 32px;
            padding-top: 0.2rem;
            padding-bottom: 0.2rem;
            font-size: 0.8125rem;
          }
        `}
      </style>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title as="div">
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
              style={{ width: 32, height: 32 }}
            >
              <FontAwesomeIcon icon="clock" />
            </span>
            <div>
              <div className="fs-0 fw-semibold">زمان‌بندی بررسی سرعت</div>
              <div className="fs--1 text-600 fw-normal">
                اجرای خودکار و ارسال خلاصه نتایج
              </div>
            </div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <hr className="my-0" />
      <Offcanvas.Body dir="rtl" className="d-flex flex-column gap-3">
        {submitted && (
          <Alert
            variant="success"
            className="mb-0 py-2"
            dismissible
            onClose={() => setSubmitted(false)}
          >
            تنظیمات زمان‌بندی ذخیره شد.
          </Alert>
        )}

        <section className="analysis-schedule-section">
          <SectionHeading
            icon="globe"
            title="آدرس‌های مورد بررسی"
            description="آدرس و محیط اجرای هر بررسی را مشخص کنید."
            aside={
              <span className="analysis-schedule-count">
              {toPersianDigits(normalizedEntries.length)}/
              {toPersianDigits(MAX_SCHEDULE_URLS)}
              </span>
            }
          />
          {entries.map(entry => {
            const trimmedUrl = entry.url.trim();
            const urlError =
              showValidation &&
              (trimmedUrl
                ? !isValidHttpUrl(trimmedUrl)
                  ? 'آدرس معتبر نیست (باید با http یا https شروع بشه).'
                  : null
                : entries.length === 1
                  ? 'حداقل یک آدرس وارد کنید'
                  : null);
            const selectedPlace =
              placeSelectOptions.find(
                option => option.value === entry.location_code
              ) || defaultPlaceOption;
            const selectedDevice =
              deviceOptions.find(option => option.value === entry.device_type) ||
              defaultDeviceOption;
            const selectedInternet =
              internetSelectOptions.find(
                option => option.value === entry.internet_type
              ) || defaultInternetOption;

            return (
              <div key={entry.id} className="analysis-schedule-entry">
                <div className="d-flex align-items-center gap-1">
                  <Form.Control
                    type="text"
                    dir={trimmedUrl ? 'ltr' : 'rtl'}
                    placeholder="https://"
                    value={entry.url}
                    isInvalid={Boolean(urlError)}
                    onChange={event =>
                      updateEntry(entry.id, { url: event.target.value })
                    }
                  />
                  {entries.length > 1 && (
                    <ChildrensTooltip html="حذف آدرس" placement="top" noChildSpan>
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 text-600 flex-shrink-0"
                        style={{ width: 24, height: 32, lineHeight: 1 }}
                        aria-label="حذف آدرس"
                        onClick={() => removeEntry(entry.id)}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </ChildrensTooltip>
                  )}
                </div>
                {urlError ? (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {urlError}
                  </Form.Control.Feedback>
                ) : null}
                <div className="d-flex gap-1 mt-1">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <Select<PlaceOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={false}
                      options={placeSelectOptions}
                      isOptionDisabled={option => Boolean(option.isDisabled)}
                      value={selectedPlace}
                      onChange={(option: SingleValue<PlaceOption>) => {
                        if (option && !option.isDisabled) {
                          updateEntry(entry.id, {
                            location_code: option.value
                          });
                        }
                      }}
                      classNamePrefix="react-select"
                      maxMenuHeight={220}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={compactSelectStyles}
                      components={{
                        Option: OptionLayoutPlace as never,
                        SingleValue: SingleValueFlag as never
                      }}
                    />
                  </div>
                  <div style={{ width: 92, flexShrink: 0 }}>
                    <Select<DeviceOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={false}
                      options={deviceOptions}
                      value={selectedDevice}
                      onChange={(option: SingleValue<DeviceOption>) => {
                        if (option) {
                          updateEntry(entry.id, {
                            device_type: option.value
                          });
                        }
                      }}
                      classNamePrefix="react-select"
                      maxMenuHeight={220}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={compactSelectStyles}
                      components={{
                        Option: OptionLayoutIcon as never,
                        SingleValue: SingleValueIcon as never
                      }}
                    />
                  </div>
                  <div style={{ width: 108, flexShrink: 0 }}>
                    <Select<InternetOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={false}
                      options={internetSelectOptions}
                      value={selectedInternet}
                      onChange={(option: SingleValue<InternetOption>) => {
                        if (option) {
                          updateEntry(entry.id, {
                            internet_type: option.value
                          });
                        }
                      }}
                      classNamePrefix="react-select"
                      maxMenuHeight={220}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={compactSelectStyles}
                      components={{
                        Option: OptionLayoutIcon as never,
                        SingleValue: SingleValueIcon as never
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="d-inline-flex align-items-center gap-1 px-0 mt-2"
            disabled={entries.length >= MAX_SCHEDULE_URLS}
            onClick={addEntry}
          >
            <FontAwesomeIcon icon="plus" />
            افزودن آدرس
          </Button>
        </section>

        <section className="analysis-schedule-section">
          <SectionHeading
            icon={enabled ? 'check-circle' : 'pause-circle'}
            title="اجرای خودکار"
            description={
              enabled
                ? 'بررسی طبق برنامه به‌صورت خودکار اجرا می‌شود.'
                : 'بررسی خودکار موقتاً متوقف است.'
            }
          />
          <div className="d-flex justify-content-end">
            <Form.Check
              type="switch"
              id="analysis-schedule-enabled"
              checked={enabled}
              onChange={event => setEnabled(event.target.checked)}
              label={enabled ? 'فعال' : 'غیرفعال'}
              reverse
            />
          </div>
        </section>

        <section className="analysis-schedule-section">
          <SectionHeading
            icon="bell"
            title="دریافت نتیجه"
            description="خلاصه نتایج پس از پایان بررسی ارسال می‌شود."
          />
          <div className="d-flex flex-wrap align-items-center gap-4">
            <Form.Check
              type="checkbox"
              id="analysis-schedule-telegram"
              checked={isSuccess && joinTelegram && telegram}
              disabled={isLoading || !isSuccess || !joinTelegram}
              onChange={event => setTelegram(event.target.checked)}
              label="تلگرام"
            />
            <Form.Check
              type="checkbox"
              id="analysis-schedule-bale"
              checked={isSuccess && joinBale && bale}
              disabled={isLoading || !isSuccess || !joinBale}
              onChange={event => setBale(event.target.checked)}
              label="بله"
            />
          </div>
          {isSuccess && (!canConfigureNotifications || missingChannels.length > 0) && (
            <Alert variant="warning" className="mt-3 mb-0 py-2 fs--2">
              {missingChannels.length > 0 ? (
                <>
                  برای تنظیم زمان‌بندی ابتدا {missingChannels.join(' و ')} را فعال
                  کنید.{' '}
                  <a
                    href={CHANNEL_BOTS_ACTIVATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="alert-link"
                  >
                    فعال‌سازی ربات‌ها
                  </a>
                </>
              ) : (
                'برای تنظیم زمان‌بندی، هر دو کانال تلگرام و بله را انتخاب کنید.'
              )}
            </Alert>
          )}
        </section>

        <section className="analysis-schedule-section">
          <SectionHeading
            icon="calendar-alt"
            title="برنامه اجرا"
            description="روز اجرای بررسی‌های خودکار را انتخاب کنید."
          />
          <Form.Group>
            <Form.Label className="fs--1 text-700">روز هفته</Form.Label>
            <Form.Select
              value={day}
              onChange={event => setDay(event.target.value)}
            >
              {weekDays.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="d-flex align-items-center gap-2 mt-3 fs--1 text-700">
            <FontAwesomeIcon icon="clock" className="text-primary" />
            <span>{scheduleSummary}</span>
          </div>
        </section>
      </Offcanvas.Body>
      <div className="offcanvas-footer d-flex gap-2">
        <Button
          variant="falcon-primary"
          className="d-flex align-items-center justify-content-center flex-grow-1"
          disabled={!entries.some(entry => entry.url.trim())}
          onClick={save}
        >
          ذخیره زمان‌بندی
        </Button>
        <Button variant="outline-secondary" className="px-4" onClick={onHide}>
          انصراف
        </Button>
      </div>
    </Offcanvas>
  );
};

export default AnalysisScheduleDrawer;
